import React, { useEffect, useState } from 'react';
import { db, auth } from '../services/firebase';
import { collection, getDocs, query, where, orderBy, limit, doc, getDoc, onSnapshot } from 'firebase/firestore';
import ChatIA from '../components/ChatIA';
import { Box, Card, CardContent, Typography, List, ListItem, ListItemText, Avatar, ListItemAvatar, Chip } from '@mui/material';
import DOMPurify from 'dompurify';
import Grid from '@mui/material/Grid'; // Importação correta para Grid v2
import PersonIcon from '@mui/icons-material/Person';
import styles from '../styles/Dashboard.module.css';

const Dashboard = () => {
  const [comunicados, setComunicados] = useState([]);
  const [reclamacoes, setReclamacoes] = useState([]);
  const [documentosVencendo, setDocumentosVencendo] = useState([]);
  const [funcionariosPresentes, setFuncionariosPresentes] = useState([]);
  const [userRole, setUserRole] = useState('');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const userCondominioId = userData.condominio_id;
          setUserRole(userData.papel);
          fetchData(userCondominioId, userData.papel, user.uid);
        }
      }
    });

    const fetchData = async (id, role, uid) => {
      if (!id) return;

      const localIsAdmin = role && ['sindico', 'sub_sindico', 'conselheiro'].includes(role);

      if (localIsAdmin) {
        // --- Lógica para Funcionários Presentes (Apenas para Admins) ---
        const funcionariosRef = collection(db, 'tb_funcionarios');
        const qFuncionarios = query(funcionariosRef, where('condominio_id', '==', id));
        const funcionariosSnapshot = await getDocs(qFuncionarios);
        const funcionariosMap = {};
        funcionariosSnapshot.forEach(doc => {
          funcionariosMap[doc.data().user_uid] = doc.data();
        });

        const pontosRef = collection(db, 'tb_pontos');
        const qPontos = query(pontosRef, where('condominio_id', '==', id));

        onSnapshot(qPontos, (snapshot) => {
          const ultimosRegistros = {};
          snapshot.forEach(doc => {
            const registro = doc.data();
            const funcionarioId = registro.funcionario_id;

            if (!ultimosRegistros[funcionarioId] || registro.timestamp.toMillis() > ultimosRegistros[funcionarioId].timestamp.toMillis()) {
              ultimosRegistros[funcionarioId] = registro;
            }
          });

          const presentes = Object.values(ultimosRegistros)
            .filter(registro => registro.tipo === 'entrada')
            .map(registro => ({
              ...funcionariosMap[registro.funcionario_id],
              id: registro.funcionario_id, // Usamos o UID como ID aqui
            }))
            .filter(f => f.id); // Garante que apenas funcionários mapeados apareçam
          setFuncionariosPresentes(presentes);
        });
      }

      try {
        // Buscar comunicados
        const comunicadosQuery = query(collection(db, 'tb_comunicados'), where('condominio_id', '==', id), orderBy('created_at', 'desc'), limit(5));
        const comunicadosSnapshot = await getDocs(comunicadosQuery);
        const comunicadosList = comunicadosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setComunicados(comunicadosList);

        // Buscar reclamações
        let reclamacoesQuery;
        if (localIsAdmin) {
          reclamacoesQuery = query(collection(db, 'tb_reclamacoes'), where('condominio_id', '==', id), orderBy('created_at', 'desc'), limit(5));
        } else {
          reclamacoesQuery = query(collection(db, 'tb_reclamacoes'), where('condominio_id', '==', id), where('autor_id', '==', uid), orderBy('created_at', 'desc'), limit(5));
        }
        const reclamacoesSnapshot = await getDocs(reclamacoesQuery);
        const reclamacoesList = reclamacoesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setReclamacoes(reclamacoesList);

        // Buscar documentos
        const documentosQuery = query(collection(db, 'tb_documentos'), where('condominio_id', '==', id));
        const documentosSnapshot = await getDocs(documentosQuery);
        const documentosList = documentosSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Filtrar documentos que estão vencendo nos próximos 30 dias
        const hoje = new Date();
        const limite = new Date();
        limite.setDate(hoje.getDate() + 30);

        const vencendo = documentosList.filter(doc => {
          if (!doc.data_vencimento) return false;
          const dataVencimento = doc.data_vencimento.toDate();
          return dataVencimento >= hoje && dataVencimento <= limite;
        }).sort((a, b) => a.data_vencimento.toDate() - b.data_vencimento.toDate()); // Ordena pelos mais próximos de vencer

        setDocumentosVencendo(vencendo);

      } catch (error) {
        console.error("Erro ao buscar dados do dashboard:", error);
      }
    };
    
    return () => unsubscribe();
  }, []);

  const formatarData = (timestamp) => {
    if (!timestamp) return 'N/A';
    const data = timestamp.toDate();
    return new Intl.DateTimeFormat('pt-BR').format(data);
  };

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: 3, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>
          Dashboard
        </Typography>
        <Grid container spacing={3}>
          {/* Coluna de Comunicados e Reclamações */}
          <Grid item md={7}>
            {isAdmin && documentosVencendo.length > 0 && (
              <>
              <Card className={styles.card} sx={{ border: '1px solid #ff9800', backgroundColor: '#fff3e0' }}>                <CardContent>
                  <Typography variant="h5" className={styles.sectionTitle} color="warning.dark">Avisos Importantes</Typography>
                  <List>
                    {documentosVencendo.map((doc) => (
                      <ListItem key={doc.id} divider>
                        <ListItemText
                          primary={`Documento a vencer: ${doc.nome}`}
                          secondary={
                            <>
                              <Typography component="span" variant="body2" display="block">
                                Vence em: {formatarData(doc.data_vencimento)}
                              </Typography>
                              {doc.estoque_nome && (
                                <Typography component="span" variant="body2" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                  Ref: {doc.estoque_nome}
                                </Typography>
                              )}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
              </>
            )}
            {isAdmin && funcionariosPresentes.length > 0 && (
              <Card className={styles.card}>
                <CardContent>
                  <Typography variant="h5" className={styles.sectionTitle}>Funcionários Presentes</Typography>
                  <List>
                    {funcionariosPresentes.length > 0 ? (
                      funcionariosPresentes.map((func) => (
                        <ListItem key={func.id} divider>
                          <ListItemAvatar>
                            <Avatar src={func.foto_url}>
                              {!func.foto_url && <PersonIcon />}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={func.nome}
                            secondary={func.cargo}
                          />
                        </ListItem>
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">Nenhum funcionário registrou entrada.</Typography>
                    )}
                  </List>
                </CardContent>
              </Card>
            )}

            <Card className={styles.card}>
              <CardContent>
                <Typography variant="h5" className={styles.sectionTitle}>Últimos Comunicados</Typography>
                <List>
                  {comunicados.map((comunicado) => (
                    <ListItem key={comunicado.id} divider>
                      <ListItemText
                        primary={comunicado.titulo}
                        primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.dark' }}
                        secondary={
                          <Typography
                            component="div"
                            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(comunicado.conteudo) }}
                          />
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
            <Card className={styles.card}>
              <CardContent>
                <Typography variant="h5" className={styles.sectionTitle}>Reclamações Recentes</Typography>
                <List>
                  {reclamacoes.map((reclamacao) => (
                    <ListItem key={reclamacao.id} divider>
                      <ListItemText
                        primary={reclamacao.titulo}
                        primaryTypographyProps={{ fontWeight: 'bold', color: 'primary.dark' }}
                        secondary={
                          <>
                            <Chip 
                              label={reclamacao.status} 
                              size="small" 
                              color={
                                reclamacao.status === 'Finalizada' ? 'success' : 
                                reclamacao.status === 'Cancelada' ? 'error' : 
                                reclamacao.status === 'Em Análise' ? 'warning' : 
                                'default'
                              }
                              sx={{ mb: 1, textTransform: 'capitalize' }}
                            />
                            <Typography
                              component="div"
                              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(reclamacao.descricao) }}
                            />
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
          {/* Coluna do Chat IA */}
          <Grid item md={5}>
            <Card className={styles.card}>
              <CardContent>
                <ChatIA />
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default Dashboard;